import React, { useEffect, useRef} from 'react'
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { localParamsRoutine, postParamRoutine, putParamRoutine } from '../../redux/Params/routines';
import { makeLocalParamsSelector, makeParamsSelector } from '../../redux/Params/selectors';
import { collectionKeysEqualityFn } from '../equality';
import { useMemo } from 'react';
import { useState } from 'react';
import shallowCompare from 'react-addons-shallow-compare';

export const useFetching = (someFetchActionCreator) => {
  const dispatch = useDispatch();
  useEffect(() => {
    dispatch(someFetchActionCreator());
  }, [])
}

export const useNotFirstRun = (value) => {
  const isFirstRun = useRef(true);
  useEffect (() => {
    isFirstRun.current = false;
  }, []);
  return isFirstRun.current ? null : value
}

export const usePrevious = (value) => {
  const ref = useRef();
  useEffect(() => {
    ref.current = value;
  });
  return ref.current;
}

export const useWhyDidYouUpdate = (name, props) => {
  // Get a mutable ref object where we can store props ...
  // ... for comparison next time this hook runs.
  const previousProps = useRef();

  useEffect(() => {
    if (previousProps.current) {
      // Get all keys from previous and current props
      const allKeys = Object.keys({ ...previousProps.current, ...props });
      // Use this object to keep track of changed props
      const changesObj = {};
      // Iterate through keys
      allKeys.forEach(key => {
        // If previous is different from current
        if (previousProps.current[key] !== props[key]) {
          // Add to changesObj
          changesObj[key] = {
            from: previousProps.current[key],
            to: props[key]
          };
        }
      });

      // If changesObj not empty then output to console
      if (Object.keys(changesObj).length) {
        console.log('[why-did-you-update]', name, changesObj);
      }
    }

    // Finally update previousProps with current props for next hook call
    previousProps.current = props;
  });
}

export const useImageLoaded = (deps = []) => {
  const [isLoaded, setIsLoaded] = React.useState(false)
  // Triggered when full image will be loaded.
  const handleImageOnLoad = React.useCallback(() => {
    setIsLoaded(true)
  }, [])

  React.useEffect(() => {
    setIsLoaded(false)
  }, deps)

  const css = {
    visibility: isLoaded ? 'visible' : 'hidden',
  }

  return { handleImageOnLoad, css }
}

export const useUserArrayParam = (type = "", initialValue = undefined) => {
  
  const paramsSelector = React.useMemo(makeParamsSelector, [])

  const  params =  useSelector(state => paramsSelector(state, type), collectionKeysEqualityFn(['id', 'updatedAt']))

  const param = params[0] || {}

  const dispatch = useDispatch()
  
  const handleAddParam = (elt) => {
    if(param && param.id) {
      dispatch(putParamRoutine({
        id: param.id,
        values: {
          value: [
            ...(param.value || []),
            elt
          ]
        }
      }))
    }
    else {
      dispatch(postParamRoutine({
        name: type,
        value: [elt]
      }))
    }
  }

  const handleRemoveParam = (indexToRemove) => {
    if(param && param.id) {
      dispatch(putParamRoutine({
        id: param.id,
        values: {
          value: [...(param.value || []).slice(0, indexToRemove), ...(param.value || []).slice(indexToRemove + 1)]
        }
      }))
    }
  }

  const handleSetParams = (params = []) => {
    if(param && param.id) {
      dispatch(putParamRoutine({
        id: param.id,
        values: {
          value: params
        }
      }))
    }
    else {
      dispatch(postParamRoutine({
        name: type,
        value: params
      }))
    }
  }

  // initialize with defaults if none exists yet
  const initializedRef = React.useRef(false)
  React.useEffect(() => {
    if (initializedRef.current) return;
    const hasParam = !!param?.id;
    const hasValues = Array.isArray(param?.value) && param.value.length > 0;
    const hasInitials = Array.isArray(initialValue) && initialValue.length > 0;
    if (!hasInitials) return;

    if (!hasParam) {
      initializedRef.current = true;
      dispatch(postParamRoutine({ name: type, value: initialValue }));
    } else if (!hasValues) {
      initializedRef.current = true;
      dispatch(putParamRoutine({ id: param.id, values: { value: initialValue } }));
    }
  }, [dispatch, param?.id, param?.updatedAt, type, Array.isArray(initialValue) ? initialValue.map((v) => (typeof v === 'string' ? v : v?.id || v?.name || JSON.stringify(v))).join('|') : ''])

  return [param.value || [], handleAddParam, handleRemoveParam, handleSetParams]
}

export const useCheckDepsUpdate = (deps = []) => {
  const compuationRef = useRef({})
  useEffect(() => {
    const changes = []
    deps.forEach((dep, index) => {
      changes[index] = dep === compuationRef.current[index] ? 0 : 1;
    }) 
    console.log('computation useCheckDepsUpdate', changes)
    deps.forEach((dep, index) => {
      compuationRef.current[index] = dep;
    })
  }, deps)
}

export function useSingleParam(name, defaultValue = {}) {
  const dispatch = useDispatch();

  const paramsSelector = useMemo(makeParamsSelector, []);
  const param = useSelector(
    (state) => paramsSelector(state, name),
    collectionKeysEqualityFn(["id", "updatedAt"])
  )?.[0];

  // état interne qui reflète le param
  const [value, setValue] = useState(param?.value || defaultValue);

  // sync du store → local
  useEffect(() => {
    if (param?.value !== undefined) {
      setValue(param.value);
    }
  }, [param]);

  // sauvegarde en base
  const save = (newValue) => {
    setValue(newValue);

    if (param) {
      dispatch(
        putParamRoutine({
          id: param.id,
          values: { value: newValue },
        })
      );
    } else {
      dispatch(
        postParamRoutine({
          name,
          value: newValue,
        })
      );
    }
  };

  return {
    value,
    setValue, // setter local seulement
    save,     // setter + save en base
    param,    // l’objet brut du store
  };
}


export function useLocalParam(key, defaultValue) {
  const dispatch = useDispatch();

  const localParamsSelector = React.useMemo(makeLocalParamsSelector, [])

  const rawValue = useSelector(
    (state) => localParamsSelector(state, key),
    shallowEqual
  )
  
  console.log('useLocalParam rawValue', rawValue)
  const value =
    (rawValue === null || rawValue === undefined) ? defaultValue
    : (defaultValue !== undefined &&
      typeof rawValue === "object" &&
      !Array.isArray(rawValue) &&
      Object.keys(rawValue).length === 0)
      ? defaultValue
      : rawValue;

  const setValue = (newValue) => {
    dispatch(
      localParamsRoutine.trigger({
        [key]: newValue,
      })
    );
  };

  return [value, setValue];
}
